import { Directive, OnInit, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import swal from "sweetalert2";

import { AuthService } from "../../security/auth.service";
import {
  ComentarioIso,
  EstadoSiguienteIso,
  HistorialIso,
  IncidenciaIsoComun,
  OpcionIso,
  PorquesIso,
  ServicioIncidenciasIso,
  TextosErrorHttpIso,
  etiquetaOpcion,
  mensajeErrorHttpIso,
  porquesDetalleIso,
} from "./incidencias-iso.comun";

/** Datos con los que el tablero abre el detalle. */
export interface DatosDetalleIso<TIncidencia> {
  idIncidenciaIa: number;
  incidencia?: TIncidencia;
  advertencia?: string;
}

/** Columna del tablero de la que el detalle toma el título y el color de cada estado. */
export interface ColumnaEstadoIso {
  estado: string;
  titulo: string;
  color: string;
}

/** Textos que cambian entre el detalle de incidencias IA y el de seguridad. */
export interface TextosDetalleIso extends TextosErrorHttpIso {
  /** Se abrió sin identificador de incidencia. */
  sinIdentificador: string;
  /** Falló la consulta del detalle. */
  errorCarga: string;
  /** La respuesta no trae la incidencia. */
  noEncontrada: string;
  /** Pasar a proceso sin el perfil que asigna. */
  soloAsignador: string;
  /** Avanzar desde Atendido sin ser quien lo revisa. */
  soloRevisor: string;
  /** Avanzar desde Proceso sin ser el responsable asignado. */
  soloResponsable: string;
}

/**
 * Detalle de una incidencia IA o de seguridad: carga con su historial y comentarios, secciones
 * plegables, avance de estado (de Abierto se pasa a la asignación del responsable; para marcar
 * Atendido se valida que esté completa) y regreso a proceso con comentario. Cada pantalla da sus
 * permisos, sus textos y lo que hace después de cambiar el estado.
 */
@Directive()
export abstract class DetalleIncidenciaIsoBase<TIncidencia extends IncidenciaIsoComun> implements OnInit {
  abstract readonly tipoEventoOptions: OpcionIso[];
  abstract readonly gravedadOptions: OpcionIso[];
  abstract readonly prioridadOptions: OpcionIso[];

  incidencia: TIncidencia | null = null;
  historial: HistorialIso[] = [];
  comentarios: ComentarioIso[] = [];
  idIncidenciaIa = 0;
  cargando = false;
  cargandoHistorial = false;
  cambiandoEstado = false;
  error = "";
  advertencia = "";
  idUsuarioSesion = 0;
  /** El usuario de la sesión tiene el perfil que asigna las incidencias (las pasa de Abierto a Proceso). */
  esAsignador = false;
  seccionesAbiertas: Record<string, boolean> = {};
  readonly data = inject<DatosDetalleIso<TIncidencia>>(MAT_DIALOG_DATA);
  protected requiereRefresh = false;
  protected readonly dialogRef = inject(MatDialogRef);
  protected abstract readonly incidenciasService: ServicioIncidenciasIso<TIncidencia>;
  protected abstract readonly columnasEstado: ColumnaEstadoIso[];
  protected abstract readonly textos: TextosDetalleIso;
  /** Nombre del perfil que asigna las incidencias. */
  protected abstract readonly perfilAsignador: string;
  /** Si al recargar se vuelve a abrir la sección que corresponde al estado (seguridad) o se respeta la que quedó abierta (IA). */
  protected abstract readonly reabrirSeccionAlCargar: boolean;
  private seccionesInicializadas = false;
  private readonly authService = inject(AuthService);

  ngOnInit(): void {
    this.cargarSesion();
    this.idIncidenciaIa = this.data?.idIncidenciaIa || this.data?.incidencia?.idIncidenciaIa || 0;
    this.incidencia = this.data?.incidencia || null;
    this.advertencia = this.data?.advertencia || "";
    this.inicializarSeccionesPorEstado();
    if (!this.idIncidenciaIa) {
      this.error = this.textos.sinIdentificador;
      return;
    }
    this.cargar();
  }

  cargar(): void {
    const tieneDatosPrevios = !!this.incidencia;
    this.cargando = !tieneDatosPrevios;
    this.cargandoHistorial = true;
    this.error = "";
    this.incidenciasService.obtener(this.idIncidenciaIa).subscribe({
      next: (res) => {
        if (!res) {
          this.mostrarProblemaCarga(this.textos.errorCarga, tieneDatosPrevios);
          this.cargando = false;
          this.cargandoHistorial = false;
          return;
        }
        if (res.codigoError) {
          this.mostrarProblemaCarga(res.mensajeError || this.textos.errorCarga, tieneDatosPrevios);
          this.cargando = false;
          this.cargandoHistorial = false;
          return;
        }
        this.incidencia = res.incidencia || this.incidencia;
        this.historial = res.historial || [];
        this.comentarios = res.comentarios || [];
        if (this.reabrirSeccionAlCargar) {
          this.seccionesInicializadas = false;
        }
        this.inicializarSeccionesPorEstado();
        if (!this.incidencia) {
          this.error = this.textos.noEncontrada;
        }
        this.cargando = false;
        this.cargandoHistorial = false;
      },
      error: (e) => {
        this.mostrarProblemaCarga(mensajeErrorHttpIso(e, this.textos.errorCarga, this.textos), tieneDatosPrevios);
        this.cargando = false;
        this.cargandoHistorial = false;
      },
    });
  }

  cambiarEstado(): void {
    const siguiente = this.siguienteEstado();
    if (!this.incidencia?.idIncidenciaIa || !siguiente) {
      return;
    }
    if (siguiente === "PROCESO") {
      if (!this.esAsignador) {
        this.error = this.textos.soloAsignador;
        return;
      }
      this.dialogRef.close({
        action: "asignacion",
        refresh: this.requiereRefresh,
        incidencia: this.incidencia,
        idUsuarioSesion: this.idUsuarioSesion,
      });
      return;
    }
    if (!this.puedeCambiarEstado()) {
      this.error = this.incidencia.estadoEvento === "ATENDIDO" ? this.textos.soloRevisor : this.textos.soloResponsable;
      return;
    }
    if (siguiente === "ATENDIDO" && !this.validarDatosParaAtendido()) {
      return;
    }
    this.enviarCambioEstado(siguiente);
  }

  siguienteEstado(): EstadoSiguienteIso | null {
    if (this.incidencia?.estadoEvento === "ABIERTO") {
      return "PROCESO";
    }
    if (this.incidencia?.estadoEvento === "PROCESO") {
      return "ATENDIDO";
    }
    if (this.incidencia?.estadoEvento === "ATENDIDO") {
      return "VOBO";
    }
    return null;
  }

  toggleSeccion(seccion: string): void {
    this.seccionesAbiertas[seccion] = !this.seccionesAbiertas[seccion];
  }

  seccionAbierta(seccion: string): boolean {
    return !!this.seccionesAbiertas[seccion];
  }

  siguienteEstadoTexto(): string {
    const siguiente = this.siguienteEstado();
    if (siguiente === "PROCESO") {
      return "Pasar a proceso";
    }
    if (siguiente === "ATENDIDO") {
      return "Marcar atendido";
    }
    if (siguiente === "VOBO") {
      return "Pasar a Vo.Bo.";
    }
    return "Estado final";
  }

  estadoTitulo(estado?: string): string {
    return this.columnasEstado.find((column) => column.estado === estado)?.titulo || estado || "-";
  }

  estadoColor(estado?: string): string {
    return this.columnasEstado.find((column) => column.estado === estado)?.color || "#64748b";
  }

  valor(value?: string | number | Date | null): string {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    return String(value);
  }

  labelGravedad(value?: string): string {
    return etiquetaOpcion(value, this.gravedadOptions);
  }

  labelPrioridad(value?: string): string {
    return etiquetaOpcion(value, this.prioridadOptions);
  }

  porquesDetalle(): Array<{ numero: number; pregunta: string; respuesta: string }> {
    const porques = porquesDetalleIso(this.incidencia?.causaOrigen);
    const keys = Object.keys(porques)
      .map(Number)
      .filter((key) => Number.isInteger(key) && key >= 1)
      .sort((a, b) => a - b);
    return keys.map((numero) => ({
      numero,
      pregunta: this.preguntaPorque(numero, porques),
      respuesta: porques[String(numero)] || "-",
    }));
  }

  trackByComentario(_index: number, comentario: ComentarioIso): number {
    return comentario.idComentarioIa || 0;
  }

  /** Si el usuario de la sesión puede avanzar la incidencia a su siguiente estado. */
  abstract puedeCambiarEstado(): boolean;

  /** Guarda el comentario del regreso y, si se guardó, regresa la incidencia a proceso. */
  protected guardarComentarioYRegresarAProceso(comentario: string): void {
    this.cambiandoEstado = true;
    this.error = "";
    this.incidenciasService
      .guardarComentario({
        idIncidenciaIa: this.incidencia?.idIncidenciaIa,
        comentario,
      })
      .subscribe({
        next: (res) => {
          if (!res || res.codigoError) {
            this.error = res?.mensajeError || "No fue posible guardar el comentario.";
            this.cambiandoEstado = false;
            return;
          }
          this.enviarCambioEstado("PROCESO");
        },
        error: (e) => {
          this.error = e?.error?.mensajeError || e?.error?.message || "No fue posible guardar el comentario.";
          this.cambiandoEstado = false;
        },
      });
  }

  /** Cambia el estado en el backend; si se cambió, `alCambiar` decide qué sigue (recargar o cerrar el detalle). */
  protected solicitarCambioEstado(
    siguiente: EstadoSiguienteIso,
    incidenciaCambio: TIncidencia | undefined,
    alCambiar: () => void,
  ): void {
    this.cambiandoEstado = true;
    this.error = "";
    this.advertencia = "";
    this.incidenciasService.cambiarEstado(this.incidencia.idIncidenciaIa, siguiente, incidenciaCambio).subscribe({
      next: (res) => {
        if (!res) {
          this.error = "No fue posible cambiar el estado.";
          this.cambiandoEstado = false;
          return;
        }
        if (res.codigoError) {
          this.error = res.mensajeError || "No fue posible cambiar el estado.";
          this.cambiandoEstado = false;
          return;
        }
        this.requiereRefresh = true;
        this.advertencia = res.advertencia || "";
        this.cambiandoEstado = false;
        alCambiar();
      },
      error: (e) => {
        this.error = e?.error?.mensajeError || e?.error?.message || "No fue posible cambiar el estado.";
        this.cambiandoEstado = false;
      },
    });
  }

  /** Si el usuario de la sesión registró la incidencia. */
  protected esUsuarioAlta(): boolean {
    return !!this.incidencia?.idUsuarioAlta && this.incidencia.idUsuarioAlta === this.idUsuarioSesion;
  }

  /** Avanza la incidencia al estado indicado. */
  protected abstract enviarCambioEstado(siguiente: EstadoSiguienteIso): void;

  /** Sección que se abre al mostrar una incidencia en el estado indicado ("" para dejarlas cerradas). */
  protected abstract seccionDefaultPorEstado(estado?: string): string;

  /** Datos que faltan para poder marcar la incidencia como atendida. */
  protected abstract faltantesParaAtendido(): string[];

  /** Pregunta del porqué indicado; la primera depende del tipo de la incidencia. */
  protected abstract preguntaPorque(numero: number, porques: PorquesIso): string;

  private cargarSesion(): void {
    const session = this.authService.getCosData();
    this.idUsuarioSesion = session?.idUsuarioSession || session?.usuario?.id || 0;
    const perfiles = session?.usuario?.perfiles || session?.datos?.listaPerfiles || [];
    this.esAsignador = perfiles.some((perfil: any) => (perfil?.nombre || perfil?.pNombre || "").trim() === this.perfilAsignador);
  }

  private inicializarSeccionesPorEstado(): void {
    if (this.seccionesInicializadas || !this.incidencia) {
      return;
    }
    const abierta = this.seccionDefaultPorEstado(this.incidencia.estadoEvento);
    this.seccionesAbiertas = {
      resumen: abierta === "resumen",
      clasificacion: abierta === "clasificacion",
      seguimiento: abierta === "seguimiento",
      historial: abierta === "historial",
    };
    this.seccionesInicializadas = true;
  }

  private mostrarProblemaCarga(mensaje: string, tieneDatosPrevios: boolean): void {
    if (tieneDatosPrevios) {
      this.advertencia = mensaje;
      return;
    }
    this.error = mensaje;
  }

  private validarDatosParaAtendido(): boolean {
    const faltantes = this.faltantesParaAtendido();
    if (!faltantes.length) {
      return true;
    }
    const lista = faltantes.map((item) => `<li>${item}</li>`).join("");
    this.error = "Completa la información requerida antes de marcar como atendido.";
    swal.fire({
      title: "Información incompleta",
      html: `<p>Para marcar la incidencia como atendida falta:</p><ul style="text-align:left;margin:12px 0 0 20px;">${lista}</ul>`,
      icon: "warning",
      confirmButtonText: "Entendido",
    });
    return false;
  }
}
